import {StopIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import React, {useCallback, useEffect, useState} from 'react'

import type {JobResult} from './content-preview-types'
import styles from './JobResultPreview.module.css'

function beforeFetchRequestHandler(event: Event) {
  // if we let the event propagate, our page-level handlers will think we're doing a page navigation
  // but we're not
  event.stopPropagation()
}

export function JobResultPreview({jobResult}: {jobResult: JobResult}) {
  const ref = React.useRef<HTMLElement>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const beforeFetchResponseHandler = useCallback((e: Event) => {
    const evt = e as CustomEvent
    if (!evt.detail.fetchResponse.succeeded) {
      setError('Failed to load markdown preview')
      // we have to stop propagation to prevent the page-level handler from thinking a page navigation has failed
      evt.preventDefault()
      evt.stopPropagation()
    }
  }, [])

  useEffect(() => {
    if (ref.current) {
      const el = ref.current
      el.addEventListener('turbo:before-fetch-request', beforeFetchRequestHandler)
      el.addEventListener('turbo:before-fetch-response', beforeFetchResponseHandler)
      // don't set src until after our event listeners are configured so we catch all the events

      setUrl(buildUrl(jobResult))
      return () => {
        el.removeEventListener('turbo:before-fetch-request', beforeFetchRequestHandler)
        el.removeEventListener('turbo:before-fetch-response', beforeFetchResponseHandler)
      }
    }
  }, [beforeFetchResponseHandler, jobResult])

  return (
    <div className={styles.container}>
      {error ? (
        <div className={styles.error}>
          <span className={styles.errorIcon}>
            <StopIcon size={24} />
          </span>
          <h2 className={styles.errorHeader}>Failed to load job results</h2>
        </div>
      ) : (
        <turbo-frame src={url} ref={ref} id="workflow_run_content" target="_top">
          <div className={styles.spinner}>
            <Spinner srText="Loading Markdown preview…" />
          </div>
        </turbo-frame>
      )}
    </div>
  )
}

function buildUrl(jobResult: JobResult) {
  const components = [
    jobResult.repoOwner,
    jobResult.repoName,
    'actions',
    'runs',
    jobResult.workflowId,
    'job',
    jobResult.jobId,
  ]
  const url = new URL(`/${components.map(String).map(encodeURIComponent).join('/')}`, window.location.origin)
  url.searchParams.set('partial', 'true')
  return url.toString()
}

interface TurboFrameProps extends React.HTMLAttributes<HTMLElement> {
  src: string | null
  target?: string
}

// convince TypeScript that <turbo-frame> does indeed exist
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'turbo-frame': React.DetailedHTMLProps<TurboFrameProps, HTMLElement>
    }
  }
  interface HTMLElementEventMap {
    'turbo:before-fetch-response': CustomEvent
  }
}
